/**
 * One expiry rule for sessions, shared by the Sessions page and the Dashboard.
 *
 * The backend leaves `status: 'active'` on a session whose expires_at has passed;
 * the Sessions table shows such a row as Expired. Anything that counts "active"
 * sessions must apply the same rule, or the Dashboard says 5 active while the
 * Sessions page shows those 5 as Expired.
 *
 * A missing or unparseable expires_at means "never expires" (the Expires column
 * prints "Never" for it).
 */
export interface SessionLike {
  status?: string | null
  expires_at?: string | null
}

export function parseExpiry(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function isSessionExpired(s: SessionLike, now: Date = new Date()): boolean {
  const exp = parseExpiry(s.expires_at)
  return exp !== null && exp.getTime() < now.getTime()
}

/** Status is active and the session has not passed its expiry. */
export function isSessionLive(s: SessionLike, now: Date = new Date()): boolean {
  return s.status === 'active' && !isSessionExpired(s, now)
}

/** Live and expiring within `windowMs` (default one hour). */
export function isSessionExpiringSoon(s: SessionLike, now: Date = new Date(), windowMs = 60 * 60 * 1000): boolean {
  const exp = parseExpiry(s.expires_at)
  if (!exp || s.status !== 'active') return false
  const left = exp.getTime() - now.getTime()
  return left > 0 && left < windowMs
}
